import { Injectable } from '@angular/core';
import { baseProjection } from '@common/projection/base.projection';
import { AppService } from '../@core/service/App.Service';
import { IDestinationCarrierInput } from '@common/models/general/destination-carrier.model';

@Injectable({ providedIn: 'root' })
export class ReadOnlyService extends AppService<any> {
  constructor() {
    super('ReadOnly');
  }

  async getAllBusiness() {
    const result = await this.get('/Business');
    return result.Data;
  }

  async getDefaultCountries<T extends baseProjection>(type: T) {
    const result = await this.get('/defaultCountries', null, { projection: type.name });
    return result?.Data;
  }

  async getCountries() {
    const result = await this.get('/countries');
    return result?.Data;
  }

  async getOrderStatusActions() {
    const result = await this.get('/OrderStatusActions');
    return result.Data;
  }

  async getRuleActionsByTypeId(typeId: number) {
    const result = await this.get(`/getRuleActionsByTypeId/${typeId}`);
    return result.Data;
  }

  async getTimeZones() {
    const result = await this.get('/timeZones');
    return result.Data;
  }

  async getCities<T extends baseProjection>(type: T, isPublicCitiesOnly: boolean = false, isDefaultCitiesOnly: boolean = false) {
    const result = await this.get('/cities', null, { projection: type.name, isPublicCitiesOnly: isPublicCitiesOnly, isDefaultCitiesOnly: isDefaultCitiesOnly });
    return result?.Data;
  }

  async getFilteredCities<T extends baseProjection>(type: T, filter) {
    const result = await this.get('/filteredCities', null, { ...filter, projection: type.name });
    return result?.Data;
  }

  async getAllEndpoints() {
    const result = await this.get('/endPoints');
    return result.Data;
  }

  async getAllPages() {
    const result = await this.get('/pages');
    return result.Data;
  }

  async getCurrencies() {
    const result = await this.get('/currencies');
    return result.Data;
  }

  async getAllRoles() {
    const result = await this.get('/roles');
    return result.Data;
  }

  async getAllSellingChannelSources() {
    const result = await this.get('/sellingChannelSources');
    return result.Data;
  }

  async getAllCompanySellingChannels() {
    const result = await this.get('/sellingChannels');
    return result.Data;
  }

  async getAllActiveCompanySellingChannels<T extends baseProjection>(type: T, companyId: string = "") {
    const result = await this.get('/getAllActiveCompanySellingChannels', null, { projection: type.name, companyId: companyId });
    return result?.Data;
  }

  async getAllStatuses() {
    const result = await this.get('/statuses');
    return result.Data;
  }

  async orderStatusesByfilter(filter) {
    const result = await this.post('/orderStatusesByfilter', filter);
    return result.Data;
  }

  async getAllSellingChannelStatuses(sourceType) {
    const result = await this.get(`/getAllSellingChannelStatuses/${sourceType}`);
    return result.Data;
  }

  async getAllCourierStatuses(sourceId: number) {
    const result = await this.get('/courierStatuses', [sourceId]);
    return result.Data;
  }

  async getAllProducts() {
    const result = await this.get('/products');
    return result.Data;
  }

  async getAllShippingSources<T extends baseProjection>(type: T) {
    const result = await this.get('/shippingSources', null, { projection: type.name });
    return result?.Data;
  }

  async getAllShippingCarriersByFilter<T extends baseProjection>(type: T, isActive: boolean = true) {
    const result = await this.get(`/shippingCarriersByFiltered`, [isActive], { projection: type.name });
    return result?.Data;
  }

  async getAllDestinationSources() {
    const result = await this.get('/destinationSources');
    return result.Data;
  }

  async getAllTags<T extends baseProjection>(type: T) {
    const result = await this.get('/tags', null, { projection: type.name });
    return result?.Data;
  }

  async getAllWarehouses<T extends baseProjection>(type: T, userId = 0) {
    const result = await this.get('/warehouses', null, { projection: type.name, userId: userId });
    return result?.Data;
  }

  async GetTransactionalAccountsByType(destination: IDestinationCarrierInput) {
    const result = await this.post(`/transactionalAccountsByType`, destination);
    return result.Data;
  }

  async GetTransactionalAccountsByClass(destination: IDestinationCarrierInput) {
    const result = await this.post(`/transactionalAccountsByClass`, destination);
    return result.Data;
  }

  async GetPayableAccounts(destination: IDestinationCarrierInput) {
    const result = await this.post(`/payableAccounts`, destination);
    return result.Data;
  }

  async GetTransactionalAccountsByClasses(destination: IDestinationCarrierInput) {
    const result = await this.post(`/transactionalAccountsByClasses`, destination);
    return result.Data;
  }

  async GetCouriers(destination: IDestinationCarrierInput) {
    const result = await this.post(`/couriers`, destination);
    return result.Data;
  }

  async GetMasterGroups(destination: IDestinationCarrierInput) {
    const result = await this.post(`/masterGroups`, destination);
    return result.Data;
  }

  async GetDetailAGroups(destination: IDestinationCarrierInput) {
    const result = await this.post(`/detailAGroups`, destination);
    return result.Data;
  }

  async GetDetailBGroups(destination: IDestinationCarrierInput) {
    const result = await this.post(`/detailBGroups`, destination);
    return result.Data;
  }

  async getFields(type: string) {
    const result = await this.get(`/Fields/${type}`);
    return result.Data;
  }

  async getDestinationCarrierByFilter(type: string) {
    const result = await this.get(`/getDestinationCarrierByFilter/${type}`);
    return result.Data;
  }

  async getCompanySetting() {
    const result = await this.get('/companySetting');
    return result.Data;
  }

  async getCompanies() {
    const result = await this.get('/companies');
    return result.Data;
  }

  async getCompany() {
    const result = await this.get('/company');
    return result.Data;
  }

  async getCashAndBankAccounts() {
    const result = await this.get('/CashAndBankAccounts');
    return result.Data;
  }

  async searchCustomers<T extends baseProjection>(type: T, searchText: string = "") {
    const result = await this.get('/searchCustomers', null, { projection: type.name, searchText: searchText });
    return result?.Data;
  }

  async getCustomersByFilter(filter: { value: string, filterType: string }) {
    const result = await this.get(`/getCustomersByFilter/${filter.value}/${filter.filterType}`);
    return result.Data;
  }

  async searchProducts<T extends baseProjection>(type: T, searchText: string = "") {
    const result = await this.get('/searchProducts', null, { projection: type.name, searchText: searchText });
    return result?.Data;
  }

  async getFilteredCouriers(destination: IDestinationCarrierInput, filter: string) {
    let params = `${destination.apiKey}/${destination.apiSecret}/${destination.destinationSlug}/${destination.mainBranchId}/${destination.destinationCarrierId}`;
    const result = await this.get(`/getFilteredCouriers/${params}/${filter}`);
    return result?.Data;
  }

  async productStock(product: any) {
    const result = await this.post(`/productStock`, JSON.stringify(product));
    return result.Data;
  }

  async getProductsStock<T extends baseProjection>(type: T, filter) {
    const result = await this.get(`/productsStock`, null, { ...filter, projection: type.name });
    return result?.Data;
  }

  async getAllStatusesForFilter() {
    const result = await this.get('/getAllStatusesForFilter');
    return result.Data;
  }

  async getActiveSubscriptionPlans<T extends baseProjection>(type: T, filter) {
    const result = await this.get('/getActiveSubscriptionPlans', null, { ...filter, projection: type.name });
    return result?.Data;
  }
}
